import {DeleteRecordsConfirmationButton} from '@flexinet/ui-components';

import {useDeletePaymentIntentsMutation} from '../hooks/usePaymentIntentsQuery';

interface DeleteRecordButtonProps {
    selectedIds: string[];
    onDeleteComplete?: () => void;
}

/**
 * Batch delete through the shared confirmation button — never a bare delete
 * (§2, DELETE_FUNCTIONALITY_PATTERN).
 *
 * FNP-918: takes the mutation directly rather than the whole list hook. The old
 * version called `usePaymentIntents()` with NO customerId purely to reach
 * `deleteRecords` — which also mounted a SECOND list query alongside the
 * table's, so every render of the page header fired a duplicate
 * `listPaymentIntents` request. The underlying service call is unchanged.
 *
 * ⚠️ MONEY PATH. `disabled` now also covers `isPending`. Previously the button
 * stayed live while the batch was in flight, so a second click re-fired the
 * whole set of cancellations — on this module that is a duplicate mutation
 * against live payment intents, not merely a wasted request.
 *
 * `onDeleteComplete` clears the selection, and now runs on SUCCESS rather than
 * unconditionally. The old version called it synchronously right after
 * `deleteRecords(...)`, so the selection cleared even when the mutation
 * FAILED — the operator was left with an error notification and no way to see
 * which rows they had been trying to act on.
 */
export const DeleteRecordButton = ({
                                       selectedIds,
                                       onDeleteComplete,
                                   }: DeleteRecordButtonProps) => {

    const deleteMutation = useDeletePaymentIntentsMutation();

    const disabled = selectedIds.length === 0 || deleteMutation.isPending;

    return (
        <DeleteRecordsConfirmationButton
            disabled={disabled}
            recordIdsLength={selectedIds.length}
            handleConfirm={() => {
                deleteMutation.mutate(selectedIds, {
                    onSuccess: () => onDeleteComplete?.(),
                });
            }}
        />
    );
};
